import React from 'react';
import { Label } from '@/components/ui/label';
import { User, Mail, Calendar, Phone } from 'lucide-react';
import SearchablePatientSelect from '../SearchablePatientSelect';
import { toTitleCase } from '@/lib/utils';
import { formatDOB } from '@/lib/dateUtils';
import { PatientSelectionProps } from './types';
import PrescriptionDataCheck from '../PrescriptionDataCheck';

const PatientSelection: React.FC<PatientSelectionProps> = ({
  selectedPatient,
  onPatientSelect,
  error,
}) => {
  return (
    <div className='mb-6 space-y-4'>
      <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
        Patient Information
      </h3>
      <div className='space-y-1.5' data-field="patient_id">
        <Label htmlFor='patient'>
          Patient <span className='text-red-500'>*</span>
        </Label>
        <SearchablePatientSelect
          onValueChange={onPatientSelect}
          hasError={!!error}
          placeholder='Search for a patient...'
        />
        {error && <p className='text-[0.8rem] text-red-500'>{error}</p>}
      </div>

      {/* Selected Patient Preview */}
      {selectedPatient && (
        <>
        <div className='rounded-lg border border-slate-200 bg-slate-50 p-4'>
          <div className='flex items-start gap-4'>
            <div className='flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand-teal/10'>
              <User className='h-5 w-5 text-brand-teal' />
            </div>
            <div className='min-w-0 flex-1'>
              <h4 className='text-base font-semibold text-slate-900'>
                {toTitleCase(selectedPatient?.full_name || '')}
              </h4>
              <div className='mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5'>
                {selectedPatient?.email && (
                  <div className='flex items-center gap-2 text-sm text-slate-600'>
                    <Mail className='h-3.5 w-3.5 text-slate-400' />
                    <span className='truncate'>{selectedPatient?.email}</span>
                  </div>
                )}
                {selectedPatient?.dob && (
                  <div className='flex items-center gap-2 text-sm text-slate-600'>
                    <Calendar className='h-3.5 w-3.5 text-slate-400' />
                    <span>{formatDOB(selectedPatient.dob)}</span>
                  </div>
                )}
                {selectedPatient?.phone && (
                  <div className='flex items-center gap-2 text-sm text-slate-600'>
                    <Phone className='h-3.5 w-3.5 text-slate-400' />
                    <span>{selectedPatient?.phone}</span>
                  </div>
                )}
                {selectedPatient?.gender && (
                  <div className='flex items-center gap-2 text-sm text-slate-600'>
                    <User className='h-3.5 w-3.5 text-slate-400' />
                    <span>{toTitleCase(selectedPatient?.gender || '')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
          
          {/* Data Completeness Check */}
          {selectedPatient?.id && (
            <PrescriptionDataCheck 
              patientId={selectedPatient.id} 
              patientName={selectedPatient.full_name}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PatientSelection;

